import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Avatar from '../common/Avatar';
import PromoBanner from './PromoBanner';
import { 
  Bell, 
  ChevronDown, 
  Phone, 
  Mail, 
  Calendar, 
  Users, 
  Settings,
  Plus,
  MessageSquare,
  CreditCard,
  FolderKanban,
  Clock,
  X,
  User,
  LogOut,
  ChevronRight,
  Megaphone,
  ExternalLink,
  Play,
  Pause,
  Timer,
  TrendingUp,
  Wallet,
  FileText,
  DollarSign,
  MapPin,
  Receipt,
  AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { useNavigate } from 'react-router-dom';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { Menu } from 'lucide-react';

const Header = ({ collapsed, setMobileOpen }) => {
  const { user, logout, isAgency, isTeamMember, isClient } = useAuth();
  const { theme, getCurrentScheduleStatus, isCopiloteMode } = useTheme();
  const navigate = useNavigate();
  const [accountManager, setAccountManager] = useState(null);
  const [favoriteCreatives, setFavoriteCreatives] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // News ticker data - loaded from backend
  const [newsItems, setNewsItems] = useState([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Team member timer & earnings data
  const [currentTimer, setCurrentTimer] = useState(null);
  const [earnings, setEarnings] = useState({ today: 0, week: 0, month: 0, year: 0 });
  const [showTimerDetails, setShowTimerDetails] = useState(false);

  // Client wallet data
  const [walletBalance, setWalletBalance] = useState(null);
  
  // Agency info for header display
  const [agencyInfo, setAgencyInfo] = useState(null);
  
  // Promo banner campaign
  const [promoCampaign, setPromoCampaign] = useState(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isClient) {
      fetchAccountManager();
      fetchFavoriteCreatives();
      fetchWalletBalance();
    }
    if (isTeamMember) {
      fetchCurrentTimer();
      fetchEarnings();
      fetchAccountManager(); // Also fetch project manager for team members
      // Poll timer every 30 seconds
      const timerInterval = setInterval(fetchCurrentTimer, 30000);
      return () => clearInterval(timerInterval);
    }
    fetchNotifications();
    fetchNews();
    fetchAgencyInfo();
    fetchPromoCampaign();
    
    // Update time every second
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Update schedule status every minute
    if (getCurrentScheduleStatus) {
      setScheduleStatus(getCurrentScheduleStatus());
      const scheduleInterval = setInterval(() => {
        setScheduleStatus(getCurrentScheduleStatus());
      }, 60000);
      return () => {
        clearInterval(timeInterval);
        clearInterval(scheduleInterval);
      };
    }
    
    return () => clearInterval(timeInterval);
  }, [isClient, isTeamMember, theme.schedule]);

  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo(res.data);
    } catch (error) {
      // Use default values
      setAgencyInfo({
        name: "MSC L'AGENCE",
        address: "12 Rue de la Communication",
        postal_code: "75001",
        city: "Paris",
        phone: "+33 1 23 45 67 89"
      });
    }
  };

  const fetchPromoCampaign = async () => {
    try {
      const res = await api.get('/settings/promo-banner');
      if (res.data && res.data.enabled) {
        setPromoCampaign(res.data);
      }
    } catch (error) {
      // No active campaign
    }
  };

  const fetchCurrentTimer = async () => {
    try {
      const res = await api.get('/time/current');
      setCurrentTimer(res.data);
    } catch (error) {
      setCurrentTimer(null);
    }
  };

  const fetchEarnings = async () => {
    try {
      // Get earnings for different periods
      const [dayRes, weekRes, monthRes, yearRes] = await Promise.all([
        api.get('/time/report', { params: { period: 'day' } }).catch(() => ({ data: { total_earnings: 0 } })),
        api.get('/time/report', { params: { period: 'week' } }).catch(() => ({ data: { total_earnings: 0 } })),
        api.get('/time/report', { params: { period: 'month' } }).catch(() => ({ data: { total_earnings: 0 } })),
        api.get('/time/report', { params: { period: 'year' } }).catch(() => ({ data: { total_earnings: 0 } }))
      ]);
      
      setEarnings({
        today: dayRes.data?.total_earnings || 0,
        week: weekRes.data?.total_earnings || 0,
        month: monthRes.data?.total_earnings || 0,
        year: yearRes.data?.total_earnings || 0
      });
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const res = await api.get('/wallet/');
      setWalletBalance(res.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  // News ticker rotation
  useEffect(() => {
    if (newsItems.length > 0) {
      const newsInterval = setInterval(() => {
        setCurrentNewsIndex((prev) => (prev + 1) % newsItems.length);
      }, 5000);
      return () => clearInterval(newsInterval);
    }
  }, [newsItems.length]);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news/active');
      if (response.data?.length > 0) {
        setNewsItems(response.data.map(n => ({
          id: n.id,
          text: n.title,
          content: n.content,
          link: n.link,
          linkText: n.link_text || 'Découvrir',
          isPromo: n.is_promo,
          promoCode: n.promo_code,
          expiresAt: n.promo_expires_at,
          limitedSpots: n.limited_spots,
          spotsRemaining: n.spots_remaining
        })));
      } else {
        // Fallback to default news if none from backend
        setNewsItems([
          { id: 1, text: "Bienvenue sur votre Espace", link: "/dashboard", linkText: "Tableau de bord" }
        ]);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNewsItems([
        { id: 1, text: "Bienvenue sur Copilote MSC", link: "/dashboard", linkText: "Tableau de bord" }
      ]);
    }
  };

  const fetchAccountManager = async () => {
    try {
      // Fetch the assigned manager for the current user (client or team member)
      const response = await api.get('/users/my-manager');
      if (response.data) {
        setAccountManager(response.data);
      }
    } catch (error) {
      console.error('Error fetching account manager:', error);
      // Fallback to agency user list
      try {
        const fallbackRes = await api.get('/users/', { params: { role: 'agency' } });
        if (fallbackRes.data?.length > 0) {
          setAccountManager(fallbackRes.data[0]);
        }
      } catch (e) {
        // Silent fail
      }
    }
  };

  const fetchFavoriteCreatives = async () => {
    setFavoriteCreatives([]);
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/');
      const notifs = (res.data || []).slice(0, 5).map(n => ({
        id: n.id,
        type: n.notification_type,
        title: n.title,
        message: n.content,
        time: formatNotifTime(n.created_at),
        read: n.read,
        link: n.link
      }));
      setNotifications(notifs);
    } catch (error) {
      // Fallback to empty
      setNotifications([]);
    }
  };

  const formatNotifTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getRoleBadge = () => {
    if (isAgency) return { label: 'Agence', color: 'bg-[var(--color-primary)] text-white' };
    if (isTeamMember) return { label: 'Équipier', color: 'bg-[#6366f1] text-white' };
    return { label: 'Client', color: 'bg-[var(--color-accent)] text-[var(--color-primary)]' };
  };

  const roleBadge = getRoleBadge();

  const formatDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const formatShortDate = (date) => {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short'
    });
  };

  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getDayName = (day) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[day] || day;
  };

  return (
    <>
      <header 
        className="fixed top-0 right-0 bg-white border-b border-[#e2e8f0] z-30 transition-all duration-300 overflow-hidden"
        style={{ 
          left: isMobile ? 0 : (collapsed ? 64 : 256)
        }}
      >
        {/* Promo Banner OR News Ticker */}
        {promoCampaign && promoCampaign.enabled ? (
          <PromoBanner campaign={promoCampaign} height="h-10" userRole={user?.role} />
        ) : (
          /* News Ticker Bar - Simplified and responsive */
          <div 
            className="h-8 md:h-10 flex items-center justify-center md:justify-between px-3 md:px-4 text-sm overflow-hidden relative z-20"
            style={{ backgroundColor: `${theme.colors?.primary}10` }}
          >
            {/* Left: Agency Info - Hidden on mobile */}
            <div className="hidden md:flex items-center gap-3 shrink-0">
              {agencyInfo && (
                <div className="flex items-center gap-3 text-xs text-[#64748b]">
                  <span className="flex items-center gap-1">
                    <Phone className="w-3 h-3" style={{ color: theme.colors?.accent }} />
                    {agencyInfo.phone}
                  </span>
                  <span className="hidden lg:flex items-center gap-1">
                    <MapPin className="w-3 h-3 opacity-50" />
                    {agencyInfo.address}, {agencyInfo.postal_code} {agencyInfo.city}
                  </span>
                </div>
              )}
            </div>
            
            {/* Center: News Ticker - Mobile friendly */}
            <div className="flex items-center gap-2 animate-fade-in flex-1 justify-center md:justify-start md:flex-none">
              <Megaphone className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" style={{ color: theme.colors?.accent }} />
              <span className="text-[#64748b] hidden sm:inline text-xs">Actu :</span>
              <span className="font-medium truncate max-w-[140px] sm:max-w-[200px] md:max-w-[400px] text-xs md:text-sm" style={{ color: theme.colors?.primary }}>
                {newsItems[currentNewsIndex]?.text}
              </span>
              {newsItems[currentNewsIndex]?.link && (
                <button 
                  onClick={() => navigate(newsItems[currentNewsIndex]?.link)}
                  className="hidden sm:flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors hover:opacity-80 shrink-0"
                  style={{ 
                    backgroundColor: theme.colors?.accent, 
                    color: theme.colors?.primary 
                  }}
                >
                  {newsItems[currentNewsIndex]?.linkText || 'Voir'}
                  <ExternalLink className="w-3 h-3" />
                </button>
              )}
            </div>
            
            {/* Right: Empty for balance - Hidden on mobile */}
            <div className="hidden md:block w-24 shrink-0" />
          </div>
        )}

        {/* Main Header - Responsive */}
        <div className="h-14 md:h-16 flex items-center justify-between px-3 md:px-6 relative z-20 bg-white">
          {/* Left side */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Mobile Menu Button - Inside header */}
            {isMobile && setMobileOpen && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileOpen(true)}
                className="md:hidden w-12 h-12 rounded-full bg-slate-100 hover:bg-slate-200"
              >
                <Menu className="h-7 w-7 text-slate-700" />
              </Button>
            )}
            
            {/* Date and Week Number - Compact on mobile */}
            <div className="flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-slate-100 rounded-full text-xs md:text-sm">
              <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-slate-500" />
              <span className="text-slate-700 font-medium capitalize">
                {formatShortDate(currentTime)}
              </span>
              <span className="text-xs text-slate-500 border-l border-slate-300 pl-1.5 md:pl-2">
                S{getWeekNumber(currentTime)}
              </span>
            </div>
            
            {/* Schedule indicator with click for details */}
            {isAgency && theme.schedule?.enabled && scheduleStatus && (
              <button 
                onClick={() => setShowScheduleModal(true)}
                className={cn(
                  "flex items-center gap-1.5 md:gap-2 px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium transition-all hover:scale-105",
                  scheduleStatus.isOpen 
                    ? "bg-green-100 text-green-700 hover:bg-green-200" 
                    : "bg-red-100 text-red-700 hover:bg-red-200"
                )}
                data-testid="schedule-indicator"
              >
                <div className={cn(
                  "w-2 h-2 rounded-full flex-shrink-0",
                  scheduleStatus.isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"
                )} />
                <span className="hidden xs:inline">{scheduleStatus.isOpen ? 'Ouvert' : 'Fermé'}</span>
                <span className="text-xs opacity-70">
                  {formatTime(currentTime)}
                </span>
              </button>
            )}

            {/* TEAM MEMBER: Timer Widget + Earnings */}
            {isTeamMember && (
              <div className="flex items-center gap-2">
                {/* Current Timer */}
                {currentTimer ? (
                  <button
                    onClick={() => setShowTimerDetails(true)}
                    className="timer-widget timer-widget-active flex items-center gap-2"
                  >
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <Timer className="w-4 h-4 text-emerald-600" />
                    <span className="font-mono font-semibold text-emerald-700">
                      {Math.floor((currentTimer.duration_minutes || 0) / 60)}h{Math.floor((currentTimer.duration_minutes || 0) % 60).toString().padStart(2, '0')}
                    </span>
                    <span className="text-xs text-emerald-600">
                      +{((currentTimer.duration_minutes || 0) / 60 * (currentTimer.hourly_rate || 0)).toFixed(0)}€
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/my-tasks')}
                    className="timer-widget flex items-center gap-2 opacity-60 hover:opacity-100"
                  >
                    <Play className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">Pas de chrono actif</span>
                  </button>
                )}

                {/* Quick Earnings Summary */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 hover:border-emerald-300 transition-all">
                      <TrendingUp className="w-4 h-4 text-emerald-600" />
                      <span className="font-mono font-semibold text-emerald-700">
                        {earnings.today.toLocaleString('fr-FR')}€
                      </span>
                      <span className="text-xs text-emerald-600">aujourd'hui</span>
                      <ChevronDown className="w-3 h-3 text-emerald-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-72">
                    <div className="p-3 border-b">
                      <h4 className="font-semibold text-sm" style={{ color: theme.colors?.primary }}>
                        Mes gains
                      </h4>
                    </div>
                    <div className="p-2 space-y-2">
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-emerald-50">
                        <span className="text-sm text-gray-600">Aujourd'hui</span>
                        <span className="font-mono font-bold text-emerald-700">
                          {earnings.today.toLocaleString('fr-FR')}€
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-cyan-50">
                        <span className="text-sm text-gray-600">Cette semaine</span>
                        <span className="font-mono font-bold text-cyan-700">
                          {earnings.week.toLocaleString('fr-FR')}€
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-violet-50">
                        <span className="text-sm text-gray-600">Ce mois</span>
                        <span className="font-mono font-bold text-violet-700">
                          {earnings.month.toLocaleString('fr-FR')}€
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-2 py-1.5 rounded-lg bg-amber-50">
                        <span className="text-sm text-gray-600">Cette année</span>
                        <span className="font-mono font-bold text-amber-700">
                          {earnings.year.toLocaleString('fr-FR')}€
                        </span>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/my-invoices')} className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                      Mes rapports d'activité
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/my-invoices?status=pending')} className="cursor-pointer">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Factures en attente
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* CLIENT: Credit Time Badge + Quick Actions */}
            {isClient && (
              <div className="flex items-center gap-2">
                {/* Credit Time Badge */}
                {walletBalance && (
                  <button
                    onClick={() => navigate('/wallet')}
                    className={cn(
                      "credit-badge",
                      walletBalance.balance_hours < 5 && walletBalance.balance_hours > 0 && "credit-badge-low",
                      walletBalance.balance_hours <= 0 && "credit-badge-critical"
                    )}
                  >
                    <Wallet className={cn(
                      "w-4 h-4",
                      walletBalance.balance_hours <= 0 ? "text-red-600" :
                      walletBalance.balance_hours < 5 ? "text-amber-600" : "text-indigo-600"
                    )} />
                    <span className={cn(
                      "font-mono font-semibold",
                      walletBalance.balance_hours <= 0 ? "text-red-700" :
                      walletBalance.balance_hours < 5 ? "text-amber-700" : "text-indigo-700"
                    )}>
                      {walletBalance.balance_hours?.toFixed(1) || 0}h
                    </span>
                    <span className={cn(
                      "text-xs hidden sm:inline",
                      walletBalance.balance_hours <= 0 ? "text-red-600" :
                      walletBalance.balance_hours < 5 ? "text-amber-600" : "text-indigo-600"
                    )}>
                      crédit
                    </span>
                  </button>
                )}

                <Button 
                  onClick={() => navigate('/my-projects/new')}
                  size="sm"
                  className="hidden sm:flex rounded-full"
                  style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Nouveau projet
                </Button>
                <Button 
                  onClick={() => navigate('/tickets/new')}
                  size="sm"
                  variant="outline"
                  className="hidden md:flex rounded-full"
                >
                  <MessageSquare className="w-4 h-4 mr-1" />
                  Ticket
                </Button>
                <Button 
                  onClick={() => navigate('/wallet')}
                  size="sm"
                  variant="outline"
                  className="hidden lg:flex rounded-full"
                >
                  <CreditCard className="w-4 h-4 mr-1" />
                  Recharger
                </Button>
              </div>
            )}

            {/* Quick Actions for Agency */}
            {isAgency && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    size="sm"
                    className="rounded-full text-white"
                    style={{ backgroundColor: theme.colors?.accent }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Action rapide</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuItem onClick={() => navigate('/projects')} className="cursor-pointer">
                    <FolderKanban className="w-4 h-4 mr-2" />
                    Nouveau projet
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/clients')} className="cursor-pointer">
                    <Users className="w-4 h-4 mr-2" />
                    Nouveau client
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/prospects')} className="cursor-pointer">
                    <Users className="w-4 h-4 mr-2" />
                    Nouveau prospect
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/receipts/pending')} className="cursor-pointer">
                    <Receipt className="w-4 h-4 mr-2" />
                    Reçus sans facture
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/contracts')} className="cursor-pointer">
                    <Clock className="w-4 h-4 mr-2" />
                    Nouveau contrat
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings/news')} className="cursor-pointer">
                    <Megaphone className="w-4 h-4 mr-2" />
                    Nouvelle actualité
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Quick Actions for team members */}
            {isTeamMember && (
              <div className="flex items-center gap-2">
                <Button 
                  onClick={() => navigate('/my-invoices/new')}
                  size="sm"
                  variant="outline"
                  className="hidden sm:flex rounded-full"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Demander paiement
                </Button>
              </div>
            )}

            {/* Favorite creatives for clients */}
            {isClient && favoriteCreatives.length > 0 && (
              <div className="hidden 2xl:flex items-center gap-2 px-3 py-1.5 bg-[#f8fafc] rounded-full">
                <Users className="h-4 w-4 text-[#64748b]" />
                <span className="text-xs text-[#64748b]">Favoris:</span>
                <div className="flex -space-x-2">
                  {favoriteCreatives.slice(0, 3).map((creative) => (
                    <Avatar 
                      key={creative.id}
                      name={creative.name}
                      size="xs"
                      type="creative"
                      className="border-2 border-white"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right side - User info */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Agency contact info - visible for clients */}
            {isClient && agencyInfo && (
              <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#f8fafc] to-[#f1f5f9] rounded-full border border-[#e2e8f0]">
                <div className="flex items-center gap-2 pr-2 border-r border-[#e2e8f0]">
                  <Phone className="h-3.5 w-3.5 text-[var(--color-accent)]" />
                  <a href={`tel:${agencyInfo.phone}`} className="text-xs font-medium text-[var(--color-primary)] hover:underline">
                    {agencyInfo.phone}
                  </a>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-[var(--color-accent)]" />
                  <span className="text-xs text-[#64748b] truncate max-w-[150px]">
                    {agencyInfo.city || 'Paris'}
                  </span>
                </div>
              </div>
            )}

            {/* Account Manager with Calendar link - visible for clients */}
            {isClient && accountManager && (
              <div className="hidden md:flex items-center gap-2 px-2 py-1 bg-[#f8fafc] rounded-full border border-[#e2e8f0]">
                <Avatar 
                  name={`${accountManager.first_name} ${accountManager.last_name}`}
                  size="xs"
                  type="agency"
                />
                <div className="hidden xl:block">
                  <p className="text-[10px] text-[#64748b] leading-none">Responsable</p>
                  <p className="text-xs font-medium text-[var(--color-primary)] leading-tight">
                    {accountManager.first_name}
                  </p>
                </div>
                <div className="flex items-center">
                  {accountManager.phone && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" asChild>
                      <a href={`tel:${accountManager.phone}`} title="Appeler">
                        <Phone className="h-3 w-3 text-[var(--color-accent)]" />
                      </a>
                    </Button>
                  )}
                  {accountManager.email && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" asChild>
                      <a href={`mailto:${accountManager.email}`} title="Email">
                        <Mail className="h-3 w-3 text-[var(--color-accent)]" />
                      </a>
                    </Button>
                  )}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6 rounded-full"
                    title="Prendre RDV"
                    asChild
                  >
                    <a 
                      href={accountManager.calendar_url || `https://calendar.google.com/calendar/appointments/schedules`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <Calendar className="h-3 w-3 text-[var(--color-accent)]" />
                    </a>
                  </Button>
                </div>
              </div>
            )}

            {/* Project Manager with Calendar link - visible for team members */}
            {isTeamMember && accountManager && (
              <div className="hidden md:flex items-center gap-2 px-2 py-1 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-full border border-violet-200">
                <Avatar 
                  name={`${accountManager.first_name} ${accountManager.last_name}`}
                  size="xs"
                  type="agency"
                />
                <div className="hidden xl:block">
                  <p className="text-[10px] text-violet-500 leading-none">Chef de projet</p>
                  <p className="text-xs font-medium text-violet-700 leading-tight">
                    {accountManager.first_name}
                  </p>
                </div>
                <div className="flex items-center">
                  {accountManager.phone && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" asChild>
                      <a href={`tel:${accountManager.phone}`} title="Appeler">
                        <Phone className="h-3 w-3 text-violet-500" />
                      </a>
                    </Button>
                  )}
                  {accountManager.email && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" asChild>
                      <a href={`mailto:${accountManager.email}`} title="Email">
                        <Mail className="h-3 w-3 text-violet-500" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Notifications dropdown */}
            <DropdownMenu open={showNotifications} onOpenChange={setShowNotifications}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-full">
                  <Bell className="h-5 w-5 text-[#64748b]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-alert)] text-white text-xs rounded-full flex items-center justify-center font-medium">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="font-semibold" style={{ color: theme.colors?.primary }}>
                    Notifications
                  </h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-xs hover:underline"
                      style={{ color: theme.colors?.accent }}
                    >
                      Tout marquer comme lu
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-[#64748b]">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucune notification</p>
                    </div>
                  ) : (
                    notifications.map((notif) => (
                      <div 
                        key={notif.id}
                        className={cn(
                          "px-4 py-3 border-b border-[#f1f5f9] hover:bg-[#f8fafc] cursor-pointer transition-colors",
                          !notif.read && "bg-[#f0fdf4]"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${theme.colors?.accent}20` }}
                          >
                            {notif.type === 'project' && <FolderKanban className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'payment' && <CreditCard className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                            {notif.type === 'brief' && <MessageSquare className="w-4 h-4" style={{ color: theme.colors?.accent }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: theme.colors?.primary }}>
                              {notif.title}
                            </p>
                            <p className="text-xs text-[#64748b] truncate">{notif.message}</p>
                          </div>
                          <span className="text-xs text-[#94a3b8] flex-shrink-0">{notif.time}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t">
                  <Button 
                    variant="ghost" 
                    className="w-full text-sm rounded-lg"
                    onClick={() => navigate('/notifications')}
                  >
                    Voir toutes les notifications
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 md:gap-3 px-2 md:px-3 py-2 rounded-full hover:bg-[#f8fafc] transition-colors">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-[var(--color-primary)]">
                      {user?.first_name}
                    </p>
                    {isClient && user?.company_name && (
                      <p className="text-xs text-[#64748b] hidden md:block">{user.company_name}</p>
                    )}
                  </div>
                  <Avatar 
                    name={`${user?.first_name} ${user?.last_name}`}
                    size="md"
                    type={isAgency ? 'agency' : isTeamMember ? 'creative' : 'client'}
                  />
                  <Badge className={cn("text-xs hidden sm:inline-flex rounded-full", roleBadge.color)}>
                    {roleBadge.label}
                  </Badge>
                  <ChevronDown className="h-4 w-4 text-[#64748b] hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2 border-b border-[#e2e8f0]">
                  <p className="font-medium text-[var(--color-primary)]">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-[#64748b]">{user?.email || user?.username}</p>
                </div>
                
                {/* Mobile quick actions for clients */}
                {isClient && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/my-projects/new')} className="sm:hidden">
                      <Plus className="h-4 w-4 mr-2" />
                      Nouveau projet
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/tickets/new')} className="md:hidden">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Ouvrir un ticket
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/wallet')} className="lg:hidden">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Recharger wallet
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="lg:hidden" />
                  </>
                )}
                
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="h-4 w-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                {isAgency && (
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="h-4 w-4 mr-2" />
                    Personnalisation
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md" aria-describedby="schedule-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Clock className="w-5 h-5" />
              Horaires de l'agence
            </DialogTitle>
          </DialogHeader>
          <p id="schedule-description" className="sr-only">Horaires d'ouverture de l'agence MSC</p>
          
          <div className="space-y-4">
            {/* Current status */}
            <div 
              className={cn(
                "p-4 rounded-xl",
                scheduleStatus?.isOpen ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-3 h-3 rounded-full",
                  scheduleStatus?.isOpen ? "bg-green-500 animate-pulse" : "bg-red-500"
                )} />
                <div>
                  <p className={cn(
                    "font-semibold",
                    scheduleStatus?.isOpen ? "text-green-700" : "text-red-700"
                  )}>
                    {scheduleStatus?.isOpen ? 'Actuellement ouvert' : 'Actuellement fermé'}
                  </p>
                  <p className="text-sm text-[#64748b]">{scheduleStatus?.message}</p>
                </div>
              </div>
            </div>

            {/* Date and time */}
            <div className="p-4 bg-[#f8fafc] rounded-xl">
              <p className="text-sm text-[#64748b]">Date et heure actuelles</p>
              <p className="text-lg font-semibold capitalize" style={{ color: theme.colors?.primary }}>
                {formatDate(currentTime)}
              </p>
              <p className="text-2xl font-mono font-bold" style={{ color: theme.colors?.accent }}>
                {formatTime(currentTime)}
              </p>
            </div>

            {/* Weekly schedule */}
            <div>
              <h4 className="font-medium mb-3" style={{ color: theme.colors?.primary }}>
                Horaires de la semaine
              </h4>
              <div className="space-y-2">
                {theme.schedule?.weeklyHours && Object.entries(theme.schedule.weeklyHours).map(([day, hours]) => {
                  const isToday = new Date().getDay() === parseInt(day);
                  return (
                    <div 
                      key={day}
                      className={cn(
                        "flex items-center justify-between px-3 py-2 rounded-lg",
                        isToday && "bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30"
                      )}
                    >
                      <span className={cn(
                        "font-medium",
                        isToday ? "text-[var(--color-primary)]" : "text-[#64748b]"
                      )}>
                        {getDayName(parseInt(day))}
                        {isToday && <span className="ml-2 text-xs">(Aujourd'hui)</span>}
                      </span>
                      <span className={cn(
                        "text-sm",
                        hours.closed ? "text-red-500" : "text-[#64748b]"
                      )}>
                        {hours.closed ? 'Fermé' : `${hours.open} - ${hours.close}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact info */}
            <div className="pt-4 border-t">
              <p className="text-sm text-[#64748b] mb-3">Besoin de nous contacter ?</p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 rounded-full"
                  asChild
                >
                  <a href={`tel:${theme.agency?.phone}`}>
                    <Phone className="w-4 h-4 mr-2" />
                    Appeler
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 rounded-full"
                  asChild
                >
                  <a href={`mailto:${theme.agency?.email}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timer Details Modal for Team Members */}
      <Dialog open={showTimerDetails} onOpenChange={setShowTimerDetails}>
        <DialogContent className="sm:max-w-md" aria-describedby="timer-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <Timer className="w-5 h-5" />
              Mon activité
            </DialogTitle>
          </DialogHeader>
          <p id="timer-description" className="sr-only">Détails de votre activité et gains</p>
          
          <div className="space-y-4">
            {/* Current Timer */}
            {currentTimer ? (
              <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Chrono en cours</span>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-green-600">Actif</span>
                  </div>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-mono font-bold text-emerald-700">
                    {Math.floor((currentTimer.duration_minutes || 0) / 60)}h
                    {Math.floor((currentTimer.duration_minutes || 0) % 60).toString().padStart(2, '0')}
                  </span>
                  <span className="text-lg font-semibold text-emerald-600">
                    +{((currentTimer.duration_minutes || 0) / 60 * (currentTimer.hourly_rate || 0)).toFixed(2)}€
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Taux: {currentTimer.hourly_rate || 0}€/h
                </p>
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-center">
                <Play className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm text-gray-600">Aucun chrono actif</p>
                <Button 
                  size="sm" 
                  className="mt-2"
                  onClick={() => {
                    setShowTimerDetails(false);
                    navigate('/my-tasks');
                  }}
                >
                  Voir mes tâches
                </Button>
              </div>
            )}

            {/* Earnings Summary */}
            <div>
              <h4 className="font-medium mb-3" style={{ color: theme.colors?.primary }}>
                Mes gains
              </h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-emerald-50 text-center">
                  <p className="text-xs text-gray-500">Aujourd'hui</p>
                  <p className="font-mono font-bold text-emerald-700">
                    {earnings.today.toLocaleString('fr-FR')}€
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-cyan-50 text-center">
                  <p className="text-xs text-gray-500">Semaine</p>
                  <p className="font-mono font-bold text-cyan-700">
                    {earnings.week.toLocaleString('fr-FR')}€
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-violet-50 text-center">
                  <p className="text-xs text-gray-500">Mois</p>
                  <p className="font-mono font-bold text-violet-700">
                    {earnings.month.toLocaleString('fr-FR')}€
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="pt-4 border-t flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={() => {
                  setShowTimerDetails(false);
                  navigate('/my-invoices');
                }}
              >
                <FileText className="w-4 h-4 mr-2" />
                Rapports
              </Button>
              <Button 
                size="sm" 
                className="flex-1"
                style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
                onClick={() => {
                  setShowTimerDetails(false);
                  navigate('/my-invoices/new');
                }}
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Demander paiement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Header;
